import React, { useRef, useEffect, useState } from "react";
import {
  useTable,
  useFilters,
  usePagination,
  useSortBy,
  useGroupBy,
  useExpanded,
} from "react-table";
import styled from "styled-components";
import { useSticky } from "react-table-sticky";
import cx from "classnames";
import { SiPivotaltracker } from "react-icons/si";

import IconSvgComponent from "components/common/IconSvgComponent";
import { UnstyledButton } from "styles/components/Btns";
import Loader from "components/common/loader";
import { IoMdArrowDropright } from "react-icons/io";

const Container = styled.div`
  .table {
    &.sticky {
      width: 100%;
      overflow-x: scroll;

      .header,
      .body {
        position: sticky;
        z-index: 1;
        width: fit-content;
      }

      .header {
        top: 0;
      }

      .body {
        position: relative;
        z-index: 0;
      }

      [data-sticky-td] {
        position: sticky;
        background-color: #ffffff;
        border: 1px solid #ffffff;
      }

      [data-sticky-last-left-td] {
        border-right: 1px solid #9797971a;
      }

      [data-sticky-first-right-td] {
        border-left: 1px solid #9797971a;
      }
    }
  }
`;

const StyledTable = styled.div``;

const StyledTableHeader = styled.div`
  border-bottom: 1px solid #9797971a;
`;

const StyledTableHeadCell = styled.div`
  padding: 12px 16px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
  overflow-x: clip;
  text-overflow: ellipsis;
  user-select: none;
  border-bottom: 2px solid transparent;
  border-top: 2px solid transparent;
  flex: 1 1 100px;
  min-width: 100px;

  ${(props) =>
    props.isSorted &&
    !props.isSortedDesc &&
    `
      border-top: 2px solid #96a0b2;
    `}

  ${(props) =>
    props.isSorted &&
    props.isSortedDesc &&
    `
      border-bottom: 2px solid #96a0b2;
  `}

  ${(props) =>
    props.isGrouped &&
    `
      // background-color: #96a0b2;
  `}

  ${(props) =>
    props.width &&
    `
    flex: 1 1 ${props.width}px;
    width: ${props.width}px;
  `}

  ${(props) =>
    props.minWidth &&
    `
    flex: 1 1 ${props.minWidth}px;
    min-width: ${props.minWidth}px;
  `}

  ${(props) =>
    props.maxWidth &&
    `
    max-width: ${props.maxWidth}px;
  `}
`;

const StyledTableBodyContainer = styled.div``;

const StyledTableRow = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #9797971a;
  transition: background-color 0.5s ease;

  ${(props) =>
    props.isSelected &&
    `
      background-color: #88c7f6;
  `}

  ${(props) =>
    props.isGrouped &&
    `
      border-bottom: 1px solid #88c7f6;
  `}
`;

const StyledTableCell = styled.div`
  padding: 12px 16px;
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  flex: 1 1 100px;
  min-width: 100px;


  ${(props) =>
    props.width &&
    `
    flex: 1 1 ${props.width}px;
    width: ${props.width}px;
  `}

  ${(props) =>
    props.minWidth &&
    `
    flex: 1 1 ${props.minWidth}px;
    min-width: ${props.minWidth}px;
  `}

  ${(props) =>
    props.maxWidth &&
    `
    max-width: ${props.maxWidth}px;
    width: ${props.minWidth}px;
  `}

  ${(props) => {
    // console.log({ props });
    return ``;
  }}
`;

const StyledTableBody = styled.div`
  width: 100%;
  position: relative;
`;

const CustomPaginationComponent = (props) => {
  const {
    pageRows,
    pageIndex,
    totalCount,
    pageSize,
    canNextPage,
    canPreviousPage,
    // handleViewLessClick,
    // handleViewAllClick,
    // showViewAllButton,
    isLoading,
    previousPage,
    nextPage,
  } = props;

  const pageMin = pageSize * pageIndex + 1;
  const pageMax = pageMin + pageRows.length - 1;

  return (
    <div style={styles.paginationContainer}>
      <div style={styles.paginationContent}>
        <div style={styles.paginationPages}>
          {!isLoading && `${pageMin}-${pageMax} of ${totalCount}`}
        </div>
        {/* {showViewAllButton && (
          <div style={styles.viewAllLessButton}>
            <ButtonLink
              disabled={localCount === pageSize ? false : localCount < pageSize ? true : false}
              style={localCount <= pageSize ? styles.paginationButtonDisabled : {}}
              isUnstyledButton
              blue={true}
              onClick={pageMax === localCount ? handleViewLessClick : handleViewAllClick}
            >
              {pageMax === localCount ? "VIEW LESS" : "VIEW ALL"}
            </ButtonLink>
          </div>
        )} */}
        <div style={styles.paginationButtons}>
          <UnstyledButton
            disabled={!canPreviousPage}
            style={!canPreviousPage ? styles.paginationButtonDisabled : {}}
            onClick={previousPage}
          >
            <IconSvgComponent iconPath="svg/page-left-icn.svg" />
          </UnstyledButton>
          <UnstyledButton
            disabled={!canNextPage}
            style={!canNextPage ? styles.paginationButtonDisabled : {}}
            onClick={nextPage}
          >
            <IconSvgComponent iconPath="svg/page-right-icn.svg" />
          </UnstyledButton>
        </div>
      </div>
    </div>
  );
};

const ArrowIcon = ({ isOpen, className }) => {
  const baseClass = "arrow";
  const classes = cx(
    baseClass,
    { [`${baseClass}--closed`]: !isOpen },
    { [`${baseClass}--open`]: isOpen },
    className
  );
  return (
    <IoMdArrowDropright className={classes} style={{ display: "flex", alignItems: "center" }} />
  );
};

const Table = (props) => {
  const {
    data,
    fetchData,
    columns,
    isLoading = true,
    isFilteringEnabled = false,
    serverSidePagination = false,
    isSortingEnabled = !serverSidePagination,
    count = 100,
    defaultPageSize = 10,
    defaultPageIndex = 0,
    defaultGroupBy = [],
    NoDataComponent,
    LoadingComponent = () => {
      return (
        <div style={{ height: "487px" }}>
          <Loader />
        </div>
      );
    },
    renderRowSubComponent,
    showPagination = true,
    isSticky = false,
    // SubComponent,
    // defaultFilterMethod = () => {},
    // defaultSorted = [],
    // getTrProps = () => {},
    // getTdProps = () => {},
    meta = {},
    classNames = "",
    style,
    enableGrouping = false,
  } = props;

  const parentRef = useRef();
  const tableBodyRef = useRef();

  const instance = useTable(
    {
      columns,
      data: data || [],

      manualFilters: serverSidePagination, // true == server side filtering
      disableFilters: !isFilteringEnabled,

      manualSortBy: serverSidePagination, // true == server side sorting
      disableSortBy: !isSortingEnabled,

      manualPagination: serverSidePagination, // true === server side pagination
      disableGroupBy: !enableGrouping,

      // Prevent react-table from auto-resetting state when data changes during
      // server-side pagination. Without these flags, react-table resets
      // pageIndex/sortBy/filters to defaults whenever the data prop changes
      // (e.g., after a fetch completes), which causes the table to snap back
      // to page 1 or lose sort/filter state. Only disable for server-side
      // pagination; client-side tables keep default auto-reset behavior.
      autoResetPage: !serverSidePagination,
      autoResetSortBy: !serverSidePagination,
      autoResetFilters: !serverSidePagination,
      // Row selection auto-reset is not applicable here — PaginatedTable does
      // not use useRowSelect with react-table; selection is managed externally
      // via meta props. Leaving autoResetSelectedRows at its default.
      autoResetSelectedRows: !serverSidePagination,

      initialState: {
        pageSize: defaultPageSize,
        pageIndex: defaultPageIndex,
        groupBy: defaultGroupBy,
      },
      pageCount: Math.ceil(count / defaultPageSize),
      meta: {
        ...meta,
        tableBodyRef,
      },
    },
    useFilters,
    useSticky,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    gotoPage,
    state: { pageSize, pageIndex, filters, groupBy, expanded },
  } = instance;

  const prevFetchDataRef = useRef(fetchData);
  const fetchGenerationRef = useRef(0);

  // Clamp pageIndex when totalCount shrinks such that the current page would
  // exceed the last valid page (e.g., last item on last page is deleted).
  // autoResetPage is false for server-side pagination, so react-table won't
  // reset automatically — we must handle this defensively.
  useEffect(() => {
    if (serverSidePagination && count > 0 && pageIndex * pageSize >= count) {
      const lastValidPage = Math.max(0, Math.floor((count - 1) / pageSize));
      if (pageIndex !== lastValidPage) {
        gotoPage(lastValidPage);
      }
    }
  }, [count, pageIndex, pageSize, serverSidePagination, gotoPage]);

  useEffect(() => {
    if (serverSidePagination) {
      const fetchDataChanged = prevFetchDataRef.current !== fetchData;
      prevFetchDataRef.current = fetchData;

      // When fetchData changes (filter/sort/account change), reset to page 0
      // before fetching. Without autoResetPage, the old pageIndex would
      // persist and fetch the wrong page after a filter change.
      if (fetchDataChanged && pageIndex !== 0) {
        gotoPage(0);
        return; // gotoPage triggers re-render → effect re-fires with pageIndex=0
      }

      // Stale-response guard: increment generation on each fetch. If a prior
      // fetch resolves after a newer one has been issued, the stale response
      // is ignored. This prevents out-of-order data from corrupting the page.
      const generation = ++fetchGenerationRef.current;
      const result = fetchData({ pageSize, pageIndex });

      if (result && typeof result.catch === "function") {
        result.catch(() => {
          // On fetch error, discard stale response but preserve the current
          // page index — do NOT reset to page 1.
          if (generation !== fetchGenerationRef.current) {
            return;
          }
        });
      }
    }
  }, [fetchData, pageIndex, pageSize, serverSidePagination, gotoPage]);

  useEffect(() => {
    if (!serverSidePagination && fetchData) {
      fetchData();
    }
  }, [fetchData, serverSidePagination]);

  return (
    <Container isSticky={isSticky} style={style} className={classNames}>
      <StyledTable {...getTableProps()} className={isSticky ? "table sticky" : ""}>
        <StyledTableHeader className="header">
          {headerGroups.map((headerGroup, index) => {
            return isFilteringEnabled ? (
              <div {...headerGroup.getHeaderGroupProps()} className="tr">
                <StyledTableRow>
                  {headerGroup.headers.map((column) => {
                    return (
                      <StyledTableHeadCell
                        className="th"
                        title={column.render("Header")}
                        isSorted={column.isSorted}
                        isSortedDesc={column.isSortedDesc}
                        {...column.getHeaderProps(
                          isSticky
                            ? {
                                width: column.minWidth,
                                maxWidth: column.maxWidth,
                                ...column.getSortByToggleProps(),
                              }
                            : {
                                minWidth: column.minWidth,
                                maxWidth: column.maxWidth,
                                ...column.getSortByToggleProps(),
                              }
                        )}
                      >
                        {column.canGroupBy ? (
                          // If the column can be grouped, let's add a toggle
                          <span {...column.getGroupByToggleProps()}>
                            {column.isGrouped ? "🛑 " : "👊 "}
                          </span>
                        ) : null}
                        {column.render("Header")}
                      </StyledTableHeadCell>
                    );
                  })}
                </StyledTableRow>
                <StyledTableRow {...headerGroup.getHeaderGroupProps({ key: `${index}-filter` })}>
                  {headerGroup.headers.map((column) => {
                    return (
                      <StyledTableHeadCell
                        {...column.getHeaderProps(
                          isSticky
                            ? {
                                width: column.width,
                                style: column.style,
                              }
                            : {
                                minWidth: column.minWidth,
                                maxWidth: column.maxWidth,
                              }
                        )}
                      >
                        {column.canFilter && column.render("Filter")}
                      </StyledTableHeadCell>
                    );
                  })}
                </StyledTableRow>
              </div>
            ) : (
              <StyledTableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => {
                  return (
                    <StyledTableHeadCell
                      className="th"
                      title={column.render("Header")}
                      isSorted={column.isSorted}
                      isSortedDesc={column.isSortedDesc}
                      isGrouped={column.isGrouped}
                      {...column.getHeaderProps(
                        isSticky
                          ? {
                              width: column.width,
                              style: column.style,
                              ...column.getSortByToggleProps(),
                            }
                          : {
                              minWidth: column.minWidth,
                              maxWidth: column.maxWidth,
                              ...column.getSortByToggleProps(),
                            }
                      )}
                    >
                      {column.canGroupBy ? (
                        // If the column can be grouped, let's add a toggle
                        <span
                          {...column.getGroupByToggleProps({
                            style: {
                              marginRight: "4px",
                              marginBottom: "-2px",
                            },
                            onClick: (event) => {
                              event.stopPropagation();
                              column.getGroupByToggleProps().onClick(event);
                            },
                          })}
                        >
                          {column.isGrouped ? (
                            <SiPivotaltracker
                              style={{
                                height: "14px",
                                width: "14px",
                                color: "#333",
                                marginBottom: "-3px",
                              }}
                            />
                          ) : (
                            <SiPivotaltracker
                              style={{
                                height: "14px",
                                width: "14px",
                                color: "#888888",
                                marginBottom: "-3px",
                              }}
                            />
                          )}
                        </span>
                      ) : null}
                      {column.render("Header")}
                    </StyledTableHeadCell>
                  );
                })}
              </StyledTableRow>
            );
          })}
        </StyledTableHeader>

        <StyledTableBodyContainer innerRef={parentRef}>
          <StyledTableBody
            {...getTableBodyProps()}
            id="tableBody"
            className="body"
            innerRef={tableBodyRef}
            style={{
              minHeight:
                serverSidePagination || isLoading ? `${53 * pageSize}px` : `${53 * page.length}px`,
            }}
          >
            {!isLoading && rows.length === 0 && NoDataComponent ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <NoDataComponent />
              </div>
            ) : null}

            {isLoading && <LoadingComponent />}

            {!isLoading &&
              page.map((row) => {
                prepareRow(row);

                return (
                  <>
                    <StyledTableRow
                      {...row.getRowProps({
                        isSelected: row.values.isSelected,
                      })}
                      className="tr"
                      isGrouped={row.isGrouped}
                    >
                      {row.cells.map((cell) => {
                        return (
                          <StyledTableCell
                            className="td"
                            title={cell.value?.toString() || ""}
                            {...cell.getCellProps(
                              isSticky
                                ? {
                                    width: cell.column.width,
                                    style: cell.column.style,
                                  }
                                : {
                                    minWidth: cell.column.minWidth,
                                    maxWidth: cell.column.maxWidth,
                                    style: cell.column.style,
                                  }
                            )}
                          >
                            {cell.isGrouped ? (
                              // If it's a grouped cell, add an expander and row count
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <span
                                  {...row.getToggleRowExpandedProps({
                                    style: { marginBottom: "1px" },
                                  })}
                                >
                                  <ArrowIcon isOpen={row.isExpanded} />
                                </span>{" "}
                                {cell.render("Cell", {
                                  tableBodyRef,
                                })}{" "}
                                ({row.subRows.length})
                              </span>
                            ) : cell.isAggregated ? (
                              // If the cell is aggregated, use the Aggregated
                              // renderer for cell
                              cell.render("Aggregated", {
                                tableBodyRef,
                              })
                            ) : cell.isPlaceholder ? null : ( // For cells with repeated values, render null
                              // Otherwise, just render the regular cell
                              cell.render("Cell", {
                                tableBodyRef,
                              })
                            )}
                          </StyledTableCell>
                        );
                      })}
                    </StyledTableRow>
                    {/* {row.isExpanded ? (
                      <div
                        style={{
                          marginTop: "12px",
                          marginLeft: "30px",
                          border: "1px solid #f5f5f5",
                        }}
                      >
                        {renderRowSubComponent({ row })}
                      </div>
                    ) : null} */}
                  </>
                );
              })}
          </StyledTableBody>
        </StyledTableBodyContainer>
      </StyledTable>
      {showPagination && (
        <CustomPaginationComponent
          pageRows={instance.page}
          pageIndex={instance.state.pageIndex}
          pageSize={instance.state.pageSize}
          canPreviousPage={instance.canPreviousPage}
          canNextPage={instance.canNextPage}
          previousPage={instance.previousPage}
          nextPage={instance.nextPage}
          totalCount={filters.length > 0 ? rows.length : serverSidePagination ? count : rows.length}
          isLoading={isLoading}
        />
      )}
    </Container>
  );
};

const styles = {
  paginationContainer: {
    padding: "12px 12px 40px 12px",
  },
  paginationContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  paginationButtons: {
    display: "flex",
    justifyContent: "space-evenly",
    width: "100px",
  },
  viewAllLessButton: {
    display: "flex",
    justifyContent: "space-evenly",
    marginLeft: "25px",
  },
  paginationButtonDisabled: {
    opacity: ".5",
  },
};

export default Table;
